<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              title="Company Management"
              subtitle="Manage and monitor all system companies"
            >
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Companies</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="form.search_keyword"
                    label="Search Companies"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    hide-details
                    @click:clear="(form.search_keyword = null), (page = 1), getData()"
                    @keydown.enter="(page = 1), getData()"
                    placeholder="Search by company name, email..."
                  ></v-text-field>
                </v-col>
                
                <!-- Status Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="form.is_active"
                    :items="statuses"
                    item-text="text"
                    item-value="value"
                    label="Status"
                    outlined
                    dense
                    clearable
                    hide-details
                    prepend-inner-icon="mdi-toggle-switch"
                    @input="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>
                
                <!-- Export Button -->
                <v-col cols="12" md="5" class="d-flex align-center justify-end">
                  <v-btn
                    color="success"
                    outlined
                    small
                    @click="generateExcel()"
                    :loading="isExcelloading"
                    :disabled="!companies.length"
                    class="px-2"
                  >
                    <v-icon small>mdi-file-excel</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table Section -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-text class="px-0 py-0">
              <!-- Loading State -->
              <div v-if="isLoading" class="text-center py-12">
                <health-loader message="Loading companies..." />
              </div>
              
              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="companies"
                :items-per-page="-1"
                hide-default-footer
                class="elevation-0"
                :class="{'empty-table': !companies.length}"
              >
                <template v-slot:[`item.serial`]="{ index }">
                  <span class="text-body-2">{{ ((page - 1) * rowsPerPage) + index + 1 }}</span>
                </template>

                <template v-slot:[`item.is_active`]="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        v-bind="attrs"
                        v-on="on"
                        x-small
                        :color="item.is_active ? 'success' : 'error'"
                        text-color="white"
                        class="font-weight-medium"
                      >
                        <v-icon left x-small>
                          {{ item.is_active ? 'mdi-check-circle' : 'mdi-close-circle' }}
                        </v-icon>
                        {{ item.is_active ? 'Active' : 'Inactive' }}
                      </v-chip>
                    </template>
                    <span>{{ item.is_active ? 'Company is Active' : 'Company is Inactive' }}</span>
                  </v-tooltip>
                </template>
                
                <template v-slot:[`item.name`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-2">
                      <v-img
                        v-if="item.logo_path"
                        :src="`${mediaUrl}${item.logo_path}`"
                        :alt="item.name"
                      ></v-img>
                      <v-icon v-else color="grey lighten-1" size="18">mdi-domain</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-body-2 font-weight-medium">
                        {{ item.name || 'No name' }}
                      </div>
                    </div>
                  </div>
                </template>
                
                <template v-slot:[`item.email`]="{ item }">
                  <div class="text-body-2">
                    <v-icon x-small class="mr-1 grey--text">mdi-email</v-icon>
                    {{ item.email || 'N/A' }}
                  </div>
                </template>
                
                <template v-slot:[`item.admin_name`]="{ item }">
                  <div class="text-body-2">
                    <v-icon x-small class="mr-1 grey--text">mdi-account</v-icon>
                    {{ item.admin_name || 'N/A' }}
                  </div>
                </template>
                
                <template v-slot:[`item.phone`]="{ item }">
                  <div class="text-body-2">
                    <v-icon x-small class="mr-1 grey--text">mdi-phone</v-icon>
                    {{ item.phone || 'N/A' }}
                  </div>
                </template>
                
                <template v-slot:[`item.address`]="{ item }">
                  <div class="text-body-2" style="max-width: 200px;">
                    <v-tooltip bottom v-if="item.address && item.address.length > 30">
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on" class="text-truncate d-block">
                          {{ item.address }}
                        </span>
                      </template>
                      <span>{{ item.address }}</span>
                    </v-tooltip>
                    <span v-else-if="item.address">{{ item.address }}</span>
                    <span v-else class="grey--text">N/A</span>
                  </div>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <div class="d-flex justify-end align-center">
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          v-bind="attrs"
                          v-on="on"
                          :to="`/companies/${encryptIt(item.id)}`"
                          icon
                          x-small
                          color="primary"
                          class="mr-1"
                        >
                          <v-icon x-small>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>Edit company</span>
                    </v-tooltip>
                  </div>
                </template>
                
                <template v-slot:no-data>
                  <empty-state icon="mdi-domain" title="No companies available" message="Create your first company to get started" />
                </template>
              </v-data-table>

              <!-- Pagination -->
              <div v-if="companies.length && paginationLength > 1" class="px-4 py-3">
                <v-divider class="mb-3"></v-divider>
                <div class="d-flex align-center justify-space-between">
                  <div class="text-body-2 grey--text">
                    Showing {{ ((page - 1) * rowsPerPage) + 1 }} to 
                    {{ Math.min(page * rowsPerPage, count) }} of {{ count }} entries
                  </div>
                  <v-pagination
                    v-model="page"
                    :length="paginationLength"
                    :total-visible="7"
                    circle
                    color="primary"
                    @input="getData()"
                  ></v-pagination>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
export default {
  name: "companiesPage",
  data() {
    return {
      form: {
        search_keyword: "",
        is_active: "",
      },
      headers: [
        {
          text: 'Sr No',
          value: 'serial',
          sortable: false,
          width: '80px',
          class: 'text-primary font-weight-bold',
          cellClass: 'text-center'
        },
        {
          text: 'Status',
          value: 'is_active',
          sortable: false,
          width: '120px',
          class: 'text-primary font-weight-bold'
        },
        {
          text: 'Company',
          value: 'name',
          sortable: false,
          class: 'text-primary font-weight-bold'
        },
        {
          text: 'Email',
          value: 'email',
          sortable: false,
          class: 'text-primary font-weight-bold'
        },
        {
          text: 'Admin',
          value: 'admin_name',
          sortable: false,
          class: 'text-primary font-weight-bold'
        },
        {
          text: 'Phone',
          value: 'phone',
          sortable: false,
          width: '140px',
          class: 'text-primary font-weight-bold'
        },
        {
          text: 'Address',
          value: 'address',
          sortable: false,
          width: '200px',
          class: 'text-primary font-weight-bold'
        },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          width: '100px',
          align: 'center',
          class: 'text-primary font-weight-bold'
        }
      ],
      page: 1,
      rowsPerPage: 15,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      companies: [],
      statuses: [
        {
          id: 0,
          text: "All Status",
          value: "",
        },
        { id: "ACTIVE", text: "Active", value: "1" },
        { id: "INACTIVE", text: "Inactive", value: "0" },
      ],
      isLoading: false,
      isExcelloading: false,
      ExportCompanies: [],
      Export_companies: [],
      delete_position: false,
      is_deleted_position: false,
    };
  },
  
  computed: {
    hasActiveFilters() {
      return (
        this.form.search_keyword ||
        this.form.is_active
      );
    },
  },
  
  watch: {
    page: "getData",
  },
  mounted() {
    this.getData();
  },
  methods: {
    getActiveFiltersCount() {
      let count = 0;
      if (this.form.search_keyword) count++;
      if (this.form.is_active !== null && this.form.is_active !== '') count++;
      return count;
    },

    clearAllFilters() {
      this.form.search_keyword = "";
      this.form.is_active = "";
      this.page = 1;
      this.getData();
    },
    async getData() {
      this.isLoading = true;
      this.form.search_keyword = this.form.search_keyword
        ? this.form.search_keyword
        : "";
      this.form.is_active = this.form.is_active ? this.form.is_active : "";
      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.rowsPerPage +
        "&search_keyword=" +
        this.form.search_keyword +
        "&is_active=" +
        this.form.is_active;
      await axios
        .get(`companies?${query}`)
        .then((response) => {
          this.companies = response.data.data;
          this.count = response.data.count;
          this.serialNoStarting = (this.page - 1) * this.rowsPerPage;
          this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
        })
        .catch(function (error) {
          console.log(error);
          window.location.href = "/auth/login";
        });
      this.isLoading = false;
    },
    async generateExcel() {
      this.isExcelLoading = true;
      this.form.search_keyword = this.form.search_keyword
        ? this.form.search_keyword
        : "";
      this.form.is_active = this.form.is_active ? this.form.is_active : "";
      let response = [];
      let query =
        "is_active=" +
        this.form.is_active +
        "&search_keyword=" +
        this.form.search_keyword;
      response = await axios
        .get(`/companies/excel_export?${query}`)
        .then((response) => window.open(`${response.data.data}`, "_blank"));
      if (response) {
        console.log(response);
      }
      this.isExcelloading = false;
    },
    async Delete_this(id) {
      this.delete_company = true;
      await axios
        .post(`/companies/delete/${this.encryptIt(id)}`)
        .then(() => {
          this.delete_company = false;
          this.is_deleted_company = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          window.location.href = "/auth/login";
        });
    },
  },
};
</script>

<style scoped>
/* Modern table styling following Products page */
.v-data-table >>> .v-data-table__wrapper {
  border-radius: 0;
}

.v-data-table >>> thead th {
  background-color: #f8f9fa !important;
  border-bottom: 1px solid #dee2e6 !important;
  color: #495057 !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  padding: 12px 16px !important;
}

.v-data-table >>> tbody tr {
  border-bottom: 1px solid #f0f0f0;
}

.v-data-table >>> tbody tr:hover {
  background-color: #f8f9fa !important;
}

.v-data-table >>> tbody td {
  padding: 12px 16px !important;
  font-size: 0.875rem;
}

/* Empty state styling */
.empty-table {
  min-height: 400px;
}

/* Filter section styling */
.v-card-subtitle {
  border-bottom: 1px solid #f0f0f0;
}

/* Consistent with Products page header */
.text-h6 {
  font-size: 1.1rem !important;
}

.text-caption {
  font-size: 0.75rem !important;
}

/* Action buttons styling */
.v-btn--icon.v-size--x-small {
  width: 28px !important;
  height: 28px !important;
}

/* Chip styling consistency */
.v-chip--x-small {
  height: 20px !important;
  font-size: 0.625rem !important;
}

/* Pagination styling */
.v-pagination {
  justify-content: center;
}

.v-pagination >>> .v-pagination__item {
  min-width: 32px !important;
  height: 32px !important;
  font-size: 0.875rem !important;
}
</style>
